import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PreviewIcon from '@mui/icons-material/Preview';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { billingApi, meterReadingsApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';

// Formattazione numeri
const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return parseFloat(num).toLocaleString('it-IT', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Formattazione valuta
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Formattazione data
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT');
};

// Colori stato billing run
const getStatoColor = (stato) => {
  switch (stato) {
    case 'bozza': return 'default';
    case 'in_corso': return 'info';
    case 'completata': return 'success';
    case 'errore': return 'warning';
    case 'annullata': return 'error';
    default: return 'default';
  }
};

const BillingTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const getInvoiceKey = (inv) => String(inv?.meter_id ?? inv?.invoice_key ?? inv?.customer_id);

  // Fatturazione semplificata: fattura tutte le letture non ancora fatturate
  const [zona, setZona] = useState('');
  const [dataEmissione, setDataEmissione] = useState(new Date().toISOString().split('T')[0]);
  const [dataScadenza, setDataScadenza] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showHistorySection, setShowHistorySection] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Stato per letture con consumo 0 selezionate (deselezionate di default)
  const [selectedZeroConsumption, setSelectedZeroConsumption] = useState(new Set());
  
  // Stato per dialog modifica lettura
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [editFormData, setEditFormData] = useState({
    lettura_mc: '',
    consumo_mc: '',
    consumo_litri: '',
    prezzo_litro: 0.45,
    aliquota_iva: 22,
    importo_gas: '',
    importo_iva: '',
    // Voci aggiuntive fattura
    tasse_accise: 0,
    quota_mensile: 0,
    mora: 0,
    spese_spedizione: 0,
    varie: 0,
    causale_varie: '',
    // Totale
    importo_totale: ''
  });

  // Stato per dialog conferma eliminazione lettura
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingReading, setDeletingReading] = useState(null);

  // Query zone
  const zonesQuery = useQuery({
    queryKey: ['billing-zones'],
    queryFn: () => billingApi.getZones().then(res => res.data?.data || [])
  });

  // Query storico emissioni (senza filtro anno)
  const runsQuery = useQuery({
    queryKey: ['billing-runs'],
    queryFn: () => billingApi.getRuns({}).then(res => res.data?.data || [])
  });

  // Mutation preview
  const previewMutation = useMutation({
    mutationFn: (data) => billingApi.preview(data),
    onSuccess: (res) => {
      setPreviewData(res.data?.data);
      setSelectedZeroConsumption(new Set()); // Reset selezioni consumo zero
      if (res.data?.data?.invoices?.length > 0) {
        setShowPreviewDialog(true);
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Nessuna lettura da fatturare per il periodo selezionato', 
          severity: 'info' 
        });
      }
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nella generazione dell\'anteprima', 
        severity: 'error' 
      });
    }
  });

  // Mutation genera fatture
  const generateMutation = useMutation({
    mutationFn: (data) => billingApi.generate(data),
    onSuccess: (res) => {
      setShowPreviewDialog(false);
      setPreviewData(null);
      setSnackbar({ 
        open: true, 
        message: res.data?.message || 'Fatture generate con successo', 
        severity: 'success' 
      });
      queryClient.invalidateQueries({ queryKey: ['billing-runs'] });
      queryClient.invalidateQueries({ queryKey: ['meters-for-entry'] });
      queryClient.invalidateQueries({ queryKey: ['meter-readings'] });
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nella generazione delle fatture', 
        severity: 'error' 
      });
    }
  });

  // Handler anteprima - fattura tutte le letture non fatturate
  const handlePreview = () => {
    previewMutation.mutate({
      zona: zona || undefined,
      data_emissione: dataEmissione
    });
  };

  // Handler genera fatture - fattura tutte le letture non fatturate
  const handleGenerate = () => {
    // Raccogli gli ID delle letture con consumo 0 da escludere (quelle non selezionate)
    const zeroConsumptionInvoices = previewData?.invoices?.filter(inv => inv.totale_litri === 0) || [];
    const excludeMeterIds = zeroConsumptionInvoices
      .filter(inv => !selectedZeroConsumption.has(getInvoiceKey(inv)))
      .map(inv => inv.meter_id)
      .filter((id) => id !== null && id !== undefined);
    
    generateMutation.mutate({
      zona: zona || undefined,
      data_emissione: dataEmissione,
      data_scadenza: dataScadenza,
      operatore: 'utente',
      exclude_meter_ids: excludeMeterIds.length > 0 ? excludeMeterIds : undefined
    });
  };

  // Toggle selezione lettura consumo zero
  const toggleZeroConsumptionSelection = (invoiceKey) => {
    setSelectedZeroConsumption(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceKey)) {
        newSet.delete(invoiceKey);
      } else {
        newSet.add(invoiceKey);
      }
      return newSet;
    });
  };

  // Seleziona/deseleziona tutte le letture consumo zero
  const toggleAllZeroConsumption = (selectAll) => {
    if (selectAll) {
      const allZeroIds = previewData?.invoices
        ?.filter(inv => inv.totale_litri === 0)
        ?.map(inv => getInvoiceKey(inv)) || [];
      setSelectedZeroConsumption(new Set(allZeroIds));
    } else {
      setSelectedZeroConsumption(new Set());
    }
  };

  // Apri dialog modifica lettura
  const handleEditReading = (reading, invoice) => {
    // Chiudi la preview dialog prima di aprire il dialog di modifica
    setShowPreviewDialog(false);
    
    setEditingReading({ ...reading, invoice });
    setEditFormData({
      lettura_mc: reading.lettura_mc || '',
      consumo_mc: (reading.consumo_litri / 4.15).toFixed(2) || '',
      consumo_litri: reading.consumo_litri || '',
      prezzo_litro: reading.prezzo_litro || invoice.readings[0]?.prezzo_litro || 0.45,
      aliquota_iva: reading.aliquota_iva || 22,
      importo_gas: reading.importo_gas || '',
      importo_iva: reading.importo_iva || '',
      // Voci aggiuntive - carica da reading o valori default
      tasse_accise: reading.tasse_accise || 0,
      quota_mensile: reading.quota_mensile || 0,
      mora: reading.mora || 0,
      spese_spedizione: reading.spese_spedizione || 0,
      varie: reading.varie || 0,
      causale_varie: reading.causale_varie || '',
      // Totale calcolato
      importo_totale: reading.importo_totale || ''
    });
    setEditDialogOpen(true);
  };

  // Ricalcola importi quando cambia consumo o altre voci
  const handleConsumoChange = (newConsumoLitri, updatedFormData = null) => {
    const formData = updatedFormData || editFormData;
    const consumoLitri = parseFloat(newConsumoLitri) || 0;
    const consumoMc = consumoLitri / 4.15;
    const prezzoLitro = parseFloat(formData.prezzo_litro) || 0.45;
    const aliquotaIva = parseFloat(formData.aliquota_iva) || 22;
    const importoGas = consumoLitri * prezzoLitro;
    const importoIva = importoGas * (aliquotaIva / 100);
    
    // Voci aggiuntive
    const tasseAccise = parseFloat(formData.tasse_accise) || 0;
    const quotaMensile = parseFloat(formData.quota_mensile) || 0;
    const mora = parseFloat(formData.mora) || 0;
    const speseSpedizione = parseFloat(formData.spese_spedizione) || 0;
    const varie = parseFloat(formData.varie) || 0;
    
    const importoTotale = importoGas + importoIva + tasseAccise + quotaMensile + mora + speseSpedizione + varie;
    
    setEditFormData(prev => ({
      ...prev,
      consumo_litri: consumoLitri,
      consumo_mc: consumoMc.toFixed(2),
      importo_gas: importoGas.toFixed(2),
      importo_iva: importoIva.toFixed(2),
      tasse_accise: tasseAccise,
      quota_mensile: quotaMensile,
      mora: mora,
      spese_spedizione: speseSpedizione,
      varie: varie,
      importo_totale: importoTotale.toFixed(2)
    }));
  };

  // Ricalcola totale quando cambiano voci aggiuntive
  const handleVoceAggiuntChange = (campo, valore) => {
    const newFormData = { ...editFormData, [campo]: valore };
    setEditFormData(newFormData);
    handleConsumoChange(editFormData.consumo_litri, newFormData);
  };

  // Mutation per aggiornare lettura
  const updateReadingMutation = useMutation({
    mutationFn: async ({ readingId, data }) => {
      return meterReadingsApi.update(readingId, data);
    },
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Lettura aggiornata', severity: 'success' });
      // Chiudi il dialog di modifica
      setEditDialogOpen(false);
      setEditingReading(null);
      // Ricarica l'anteprima (sarà ricaricato automaticamente)
      previewMutation.mutate({
        zona: zona || undefined,
        data_emissione: dataEmissione
      });
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore aggiornamento lettura', 
        severity: 'error' 
      });
    }
  });

  // Salva modifica lettura
  const handleSaveReading = () => {
    if (!editingReading) return;
    
    updateReadingMutation.mutate({
      readingId: editingReading.reading_id,
      data: {
        consumo_litri: parseFloat(editFormData.consumo_litri),
        consumo_mc: parseFloat(editFormData.consumo_mc),
        prezzo_litro: parseFloat(editFormData.prezzo_litro),
        aliquota_iva: parseFloat(editFormData.aliquota_iva),
        importo_gas: parseFloat(editFormData.importo_gas),
        importo_iva: parseFloat(editFormData.importo_iva),
        // Voci aggiuntive
        tasse_accise: parseFloat(editFormData.tasse_accise) || 0,
        quota_mensile: parseFloat(editFormData.quota_mensile) || 0,
        mora: parseFloat(editFormData.mora) || 0,
        spese_spedizione: parseFloat(editFormData.spese_spedizione) || 0,
        varie: parseFloat(editFormData.varie) || 0,
        causale_varie: editFormData.causale_varie || '',
        importo_totale: parseFloat(editFormData.importo_totale)
      }
    });
  };

  // Mutation per eliminare lettura
  const deleteReadingMutation = useMutation({
    mutationFn: async (readingId) => {
      return meterReadingsApi.delete(readingId);
    },
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Lettura eliminata con successo', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingReading(null);
      // Ricarica l'anteprima
      previewMutation.mutate({
        zona: zona || undefined,
        data_emissione: dataEmissione
      });
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore eliminazione lettura', 
        severity: 'error' 
      });
    }
  });

  // Handler per aprire dialog eliminazione
  const handleDeleteReading = (reading, invoice) => {
    setShowPreviewDialog(false);
    setDeletingReading({ ...reading, invoice });
    setDeleteDialogOpen(true);
  };

  // Conferma eliminazione
  const handleConfirmDelete = () => {
    if (!deletingReading) return;
    deleteReadingMutation.mutate(deletingReading.reading_id);
  };

  // Separa fatture normali da quelle con consumo zero e note di credito
  const invoicesWithConsumption = previewData?.invoices?.filter(inv => inv.totale_litri > 0) || [];
  const invoicesZeroConsumption = previewData?.invoices?.filter(inv => inv.totale_litri === 0) || [];
  const invoicesCreditNote = previewData?.invoices?.filter(inv => inv.totale_litri < 0) || [];

  return (
    <Box>
      {/* Form emissione */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          Nuova Emissione Fatture
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Zona</InputLabel>
              <Select
                value={zona}
                label="Zona"
                onChange={(e) => setZona(e.target.value)}
              >
                <MenuItem value="">Tutte le zone</MenuItem>
                {(zonesQuery.data || []).map(z => (
                  <MenuItem key={z} value={z}>Zona {z}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Emissione"
              type="date"
              value={dataEmissione}
              onChange={(e) => setDataEmissione(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Scadenza"
              type="date"
              value={dataScadenza}
              onChange={(e) => setDataScadenza(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              fullWidth
              sx={{ height: '100%' }}
            >
              Anteprima
            </Button>
          </Grid>
        </Grid>

        {previewMutation.isPending && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>

      {/* Storico emissioni */}
      <Accordion expanded={showHistorySection} onChange={() => setShowHistorySection(!showHistorySection)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Storico Emissioni</Typography>
            <Tooltip title="Aggiorna">
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); runsQuery.refetch(); }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {runsQuery.isLoading && <LinearProgress />}
          
          {runsQuery.data?.length === 0 && !runsQuery.isLoading && (
            <Alert severity="info">
              Nessuna emissione trovata
            </Alert>
          )}

          {runsQuery.data?.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Data</TableCell>
                    <TableCell>Periodo</TableCell>
                    <TableCell>Zona</TableCell>
                    <TableCell align="right">N° Fatture</TableCell>
                    <TableCell align="right">Numeri Fattura</TableCell>
                    <TableCell align="right">Totale Litri</TableCell>
                    <TableCell align="right">Totale Importo</TableCell>
                    <TableCell>Stato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runsQuery.data.map(run => (
                    <TableRow key={run.id} hover>
                      <TableCell>{formatDate(run.data_emissione)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {run.periodo_riferimento}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {run.zona ? `Zona ${run.zona}` : 'Tutte'}
                      </TableCell>
                      <TableCell align="right">
                        {run.num_fatture_generate}
                        {run.num_errori > 0 && (
                          <Tooltip title={`${run.num_errori} errori`}>
                            <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {run.nuf_da && run.nuf_a ? `${formatNuf(run.sezionale, run.nuf_da)} - ${formatNuf(run.sezionale, run.nuf_a)}` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(run.totale_litri, 0)} L
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(run.totale_importo)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={run.stato} 
                          size="small" 
                          color={getStatoColor(run.stato)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Dialog Anteprima */}
      <Dialog 
        open={showPreviewDialog} 
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="primary" />
            Anteprima Fatture - {previewData?.stats?.periodo}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Statistiche */}
          {previewData?.stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Letture
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {previewData.stats.total_readings}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Fatture da generare
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {previewData.stats.total_invoices ?? previewData.stats.total_customers}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Totale Litri
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {formatNumber(previewData.stats.total_litri, 0)} L
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Totale Importo
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency(previewData.stats.total_importo)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Warning anomalie */}
          {previewData?.stats?.readings_with_anomaly > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <WarningIcon fontSize="small" sx={{ mr: 1 }} />
              {previewData.stats.readings_with_anomaly} letture con consumo anomalo
            </Alert>
          )}

          {/* Sezione 1: Fatture con consumo > 0 (saranno fatturate) */}
          {invoicesWithConsumption.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                Fatture da emettere ({invoicesWithConsumption.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'success.50' }}>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Indirizzo</TableCell>
                      <TableCell align="right">Insoluto</TableCell>
                      <TableCell align="right">N° Letture</TableCell>
                      <TableCell align="right">Litri</TableCell>
                      <TableCell align="right">Imponibile</TableCell>
                      <TableCell align="right">IVA</TableCell>
                      <TableCell align="right">Totale Fattura</TableCell>
                      <TableCell align="center">Modifica</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicesWithConsumption.map(inv => (
                      <TableRow key={getInvoiceKey(inv)} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {inv.customer_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.customer_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {inv.customer_address}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {inv.insoluto > 0 ? (
                            <Chip 
                              label={formatCurrency(inv.insoluto)} 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          ) : (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {inv.readings.length}
                          {inv.readings.some(r => r.is_anomaly) && (
                            <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(inv.totale_litri, 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(inv.totale_imponibile)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(inv.totale_iva)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {formatCurrency(inv.totale_fattura)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {inv.readings.map((reading) => (
                            <Box key={reading.reading_id} sx={{ display: 'inline-flex', gap: 0.5 }}>
                              <Tooltip title={`Modifica contatore ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleEditReading(reading, inv)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Elimina lettura ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteReading(reading, inv)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Sezione 2: Letture con consumo 0 (deselezionate di default) */}
          {invoicesZeroConsumption.length > 0 && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ⚠️ Letture con consumo ZERO ({invoicesZeroConsumption.length})
                </Typography>
                <Typography variant="body2">
                  Queste letture hanno consumo 0 e non verranno fatturate per default. 
                  Seleziona quelle che vuoi comunque fatturare.
                </Typography>
              </Alert>
              
              <Box sx={{ mb: 1, display: 'flex', gap: 2 }}>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => toggleAllZeroConsumption(true)}
                >
                  Seleziona tutte
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => toggleAllZeroConsumption(false)}
                >
                  Deseleziona tutte
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                  Selezionate: {selectedZeroConsumption.size} / {invoicesZeroConsumption.length}
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'warning.50' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'warning.100' }}>
                      <TableCell padding="checkbox">Fattura</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Indirizzo</TableCell>
                      <TableCell align="right">N° Letture</TableCell>
                      <TableCell align="right">Litri</TableCell>
                      <TableCell align="right">Importo</TableCell>
                      <TableCell align="center">Modifica</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicesZeroConsumption.map(inv => (
                      <TableRow 
                        key={getInvoiceKey(inv)} 
                        hover
                        sx={{ 
                          opacity: selectedZeroConsumption.has(getInvoiceKey(inv)) ? 1 : 0.6,
                          bgcolor: selectedZeroConsumption.has(getInvoiceKey(inv)) ? 'success.50' : 'inherit'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedZeroConsumption.has(getInvoiceKey(inv))}
                            onChange={() => toggleZeroConsumptionSelection(getInvoiceKey(inv))}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {inv.customer_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.customer_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {inv.customer_address}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {inv.readings.length}
                        </TableCell>
                        <TableCell align="right">
                          <Chip label="0" size="small" color="warning" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(inv.totale_fattura)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {inv.readings.map((reading) => (
                            <Box key={reading.reading_id} sx={{ display: 'inline-flex', gap: 0.5 }}>
                              <Tooltip title={`Modifica contatore ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleEditReading(reading, inv)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Elimina lettura ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteReading(reading, inv)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Sezione 3: Note di credito (consumo < 0) */}
          {invoicesCreditNote.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  📝 Note di Credito ({invoicesCreditNote.length})
                </Typography>
                <Typography variant="body2">
                  Queste sono note di credito con importo negativo che verranno generate normalmente.
                </Typography>
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'info.50' }}>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Indirizzo</TableCell>
                      <TableCell align="right">Insoluto</TableCell>
                      <TableCell align="right">N° Letture</TableCell>
                      <TableCell align="right">Litri (Negativo)</TableCell>
                      <TableCell align="right">Imponibile</TableCell>
                      <TableCell align="right">IVA</TableCell>
                      <TableCell align="right">Totale Nota</TableCell>
                      <TableCell align="center">Modifica</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicesCreditNote.map(inv => (
                      <TableRow key={getInvoiceKey(inv)} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {inv.customer_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.customer_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {inv.customer_address}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {inv.insoluto > 0 ? (
                            <Chip 
                              label={formatCurrency(inv.insoluto)} 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          ) : (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {inv.readings.length}
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error" fontWeight="bold">
                            {formatNumber(inv.totale_litri, 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error">
                            {formatCurrency(inv.totale_imponibile)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error">
                            {formatCurrency(inv.totale_iva)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            {formatCurrency(inv.totale_fattura)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {inv.readings.map((reading) => (
                            <Box key={reading.reading_id} sx={{ display: 'inline-flex', gap: 0.5 }}>
                              <Tooltip title={`Modifica contatore ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleEditReading(reading, inv)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Elimina lettura ${reading.meter_number}`}>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteReading(reading, inv)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowPreviewDialog(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? 'Generazione in corso...' : 'Genera Fatture'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modifica Lettura */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            Modifica Lettura
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {editingReading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Cliente:</strong> {editingReading.invoice?.customer_name}<br />
                  <strong>Contatore:</strong> {editingReading.meter_number}
                </Typography>
              </Alert>

              <TextField
                label="Consumo (Litri)"
                type="number"
                value={editFormData.consumo_litri}
                onChange={(e) => handleConsumoChange(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>L</Typography>
                }}
              />

              <TextField
                label="Consumo (MC)"
                type="number"
                value={editFormData.consumo_mc}
                fullWidth
                size="small"
                disabled
                InputProps={{
                  endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>MC</Typography>
                }}
              />

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Prezzo/Litro"
                    type="number"
                    value={editFormData.prezzo_litro}
                    onChange={(e) => {
                      setEditFormData(prev => ({ ...prev, prezzo_litro: e.target.value }));
                      // Ricalcola con nuovo prezzo
                      setTimeout(() => handleConsumoChange(editFormData.consumo_litri), 0);
                    }}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Aliquota IVA"
                    type="number"
                    value={editFormData.aliquota_iva}
                    onChange={(e) => {
                      setEditFormData(prev => ({ ...prev, aliquota_iva: e.target.value }));
                      setTimeout(() => handleConsumoChange(editFormData.consumo_litri), 0);
                    }}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>%</Typography>
                    }}
                  />
                </Grid>
              </Grid>

              <Divider />

              {/* Importi calcolati (gas e IVA) */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Importi Gas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Imponibile Gas"
                    type="number"
                    value={editFormData.importo_gas}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="IVA Gas"
                    type="number"
                    value={editFormData.importo_iva}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Voci aggiuntive modificabili */}
              <Typography variant="subtitle2" color="text.secondary">
                Voci Aggiuntive
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Tasse/Accise"
                    type="number"
                    value={editFormData.tasse_accise}
                    onChange={(e) => handleVoceAggiuntChange('tasse_accise', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Quota Mensile"
                    type="number"
                    value={editFormData.quota_mensile}
                    onChange={(e) => handleVoceAggiuntChange('quota_mensile', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Mora/Interessi"
                    type="number"
                    value={editFormData.mora}
                    onChange={(e) => handleVoceAggiuntChange('mora', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Spese Spedizione/Bollo"
                    type="number"
                    value={editFormData.spese_spedizione}
                    onChange={(e) => handleVoceAggiuntChange('spese_spedizione', e.target.value)}
                    fullWidth
                    size="small"
                    helperText="Escluso IVA art. 15 DPR 633/72"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Varie"
                    type="number"
                    value={editFormData.varie}
                    onChange={(e) => handleVoceAggiuntChange('varie', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Causale Varie"
                    value={editFormData.causale_varie}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, causale_varie: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Voci a livello fattura (solo visualizzazione) */}
              {(editingReading?.invoice?.totale_interventi > 0 || (editingReading?.invoice?.totale_varie && editingReading?.invoice?.totale_varie < 0)) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Voci a livello Fattura (non modificabili da qui)
                  </Typography>
                  <Grid container spacing={2}>
                    {editingReading?.invoice?.totale_interventi > 0 && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Interventi Extra:</strong> {formatCurrency(editingReading.invoice.totale_interventi)}
                            {editingReading.invoice.causale_interventi && (
                              <Typography component="span" variant="caption" display="block" color="text.secondary">
                                {editingReading.invoice.causale_interventi}
                              </Typography>
                            )}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {editingReading?.invoice?.totale_varie < 0 && (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ py: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Sconto L.448/99:</strong> {formatCurrency(editingReading.invoice.totale_varie)}
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {editingReading.invoice.causale_varie || 'Sconto Legge 448/99 art.8 comma 10'}
                            </Typography>
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Totale finale */}
              <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      TOTALE FATTURA
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      value={editFormData.importo_totale}
                      fullWidth
                      size="small"
                      disabled
                      InputProps={{
                        startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>€</Typography>
                      }}
                      sx={{ 
                        '& .MuiInputBase-input': { fontWeight: 'bold', color: 'primary.main', fontSize: '1.2rem' },
                        bgcolor: 'white'
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveReading}
            disabled={updateReadingMutation.isPending}
          >
            {updateReadingMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog conferma eliminazione lettura */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Stai per eliminare la lettura del contatore <strong>{deletingReading?.meter_number}</strong>.
            Questa azione non può essere annullata.
          </Alert>
          <Typography variant="body2">
            Cliente: <strong>{deletingReading?.invoice?.customer_name}</strong><br />
            Consumo: <strong>{formatNumber(deletingReading?.consumo_litri, 2)} Lt</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteReadingMutation.isPending}
          >
            {deleteReadingMutation.isPending ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingTab;
